import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiPlus, FiTrash2, FiRefreshCw, FiUsers, FiUserCheck, FiUserX, FiMessageSquare, FiUser, FiPhone, FiSearch, FiMoon, FiSun, FiArrowLeft } from 'react-icons/fi';
import { useApp } from '../context/AppContext';
import { playDeleteSound } from '../utils/notificationSound';
import { formatLastSeen } from '../utils/timeUtils';
import { useNdTheme } from '../utils/ndTheme';
import { ENABLE_MODAL_SWIPE_BACK } from '../utils/mobileModalSwipe';
import '../styles/newDesign.css';

const ND_CARD_COLORS = ['#2dd4bf', '#38bdf8', '#a78bfa', '#fbbf24', '#f0566a', '#34d399', '#f472b6', '#f97316'];

function ndColorOf(name) {
  const s = String(name || '?');
  let sum = 0;
  for (let i = 0; i < s.length; i += 1) sum += s.charCodeAt(i);
  return ND_CARD_COLORS[sum % ND_CARD_COLORS.length];
}

function ndInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts[1] ? parts[1][0] : '')).toUpperCase();
}

function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

export default function ChatsListModalMobile({ open, onClose, onOpenChat, onOpenCreateChat }) {
  const { state, dispatch } = useApp();
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const chats = state.chats || [];
  const currentChatId = state.currentChat?.id;
  const [deletingChats, setDeletingChats] = useState(new Set());
  // Копия удаляемого чата — чтобы вернуть его в список, если сервер откажет
  const deletedChatBackup = useRef(null);

  // Состояния для сотрудников
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState('all'); // 'all', 'online', 'offline'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingPrivateTargets, setPendingPrivateTargets] = useState(new Set());
  const [now, setNow] = useState(() => new Date());

  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  // Функция для определения онлайн статуса пользователя
  const isUserOnline = (userId) => {
    if (!state.onlineUsers || !Array.isArray(state.onlineUsers)) return false;
    return state.onlineUsers.some(user => {
      const entryId = typeof user === 'object' ? (user.id || user.userId) : user;
      return String(entryId) === String(userId);
    });
  };

  // Хелпер: определяем, что чат — приватный 1:1
  const isOneToOnePrivate = (chat) => {
    if (!chat) return false;
    if (chat.isPrivate) return true;
    if (chat.type === 'private') return true;
    const twoParticipants = Array.isArray(chat.participants) && chat.participants.length === 2;
    if (twoParticipants && !chat.isGroup) return true;
    if (typeof chat.name === 'string' && chat.name.trim().toLowerCase() === 'приватный чат') return true;
    return false;
  };

  // Скрываем из секции "Ваши чаты" все приватные 1:1 чаты
  const visibleChats = (chats || []).filter(chat => !isOneToOnePrivate(chat));

  useEffect(() => {
    if (!open) return undefined;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [open]);

  // Загрузка сотрудников
  useEffect(() => {
    if (!open) return;
    if (!window.socket) return;

    const refreshData = () => {
      window.socket.emit('get_all_users');
      window.socket.emit('get_online_users');
    };

    refreshData();

    const handleAllUsers = (users) => {
      setEmployees(users || []);
    };

    window.socket.on('all_users', handleAllUsers);

    return () => {
      window.socket.off('all_users', handleAllUsers);
    };
  }, [open]);

  // Обновление списка пользователей при изменении onlineUsers
  useEffect(() => {
    if (!open) return;
    if (!window.socket || !window.socket.connected) return;
    window.socket.emit('get_all_users');
  }, [state.onlineUsers, open]);

  // Слушаем создание/приход чатов от сервера
  useEffect(() => {
    if (!window.socket) return;

    const onNewChat = (newChat) => {
      if (!newChat) return;
      if (newChat.isPrivate && Array.isArray(newChat.participants)) {
        const other = newChat.participants.find(p => p.id !== state.user?.id);
        if (other) {
          setPendingPrivateTargets(prev => {
            if (!prev.has(other.id)) return prev;
            const next = new Set(prev);
            next.delete(other.id);
            return next;
          });
        }
      }
    };

    const onPrivateChatCreated = (data) => {
      if (!data || !data.chat) return;

      if (data.chat.isPrivate && Array.isArray(data.chat.participants)) {
        const other = data.chat.participants.find(p => p.id !== state.user?.id);
        if (other) {
          setPendingPrivateTargets(prev => {
            const next = new Set(prev);
            next.delete(other.id);
            return next;
          });
        }
      }

      dispatch({ type: 'SET_CURRENT_CHAT', payload: data.chat });
      dispatch({ type: 'SET_MESSAGES', payload: [] });
      dispatch({ type: 'MARK_CHAT_AS_READ', payload: { chatId: data.chat.id, participants: data.chat.participants } });

      if (window.socket && window.socket.connected) {
        window.socket.emit('join_chat', data.chat.id);
        window.socket.emit('get_chat_messages', data.chat.id);
        window.socket.emit('get_pinned_messages', data.chat.id);
        window.socket.emit('mark_chat_read', data.chat.id);
      }

      if (onOpenChat) onOpenChat();
      onClose();
    };

    window.socket.on('chat_created', onNewChat);
    window.socket.on('private_chat_created', onPrivateChatCreated);

    return () => {
      window.socket.off('chat_created', onNewChat);
      window.socket.off('private_chat_created', onPrivateChatCreated);
    };
  }, [state.user?.id, dispatch, onOpenChat, onClose]);

  // Обработчики событий удаления чата
  useEffect(() => {
    if (!window.socket) return;

    const handleDeleteError = (error) => {
      console.error('Ошибка удаления чата:', error);
      alert('Не удалось удалить чат. Попробуйте еще раз.');
      setDeletingChats(new Set());

      if (deletedChatBackup.current && window.socket) {
        window.socket.emit('get_chats');
      }
      deletedChatBackup.current = null;
    };

    const handleChatDeleted = (data) => {
      setDeletingChats(prev => {
        const newSet = new Set(prev);
        newSet.delete(data.chatId);
        return newSet;
      });

      deletedChatBackup.current = null;

      if (window.socket) {
        window.socket.emit('get_chats');
      }
    };

    window.socket.on('chat_delete_error', handleDeleteError);
    window.socket.on('chat_deleted', handleChatDeleted);

    return () => {
      window.socket.off('chat_delete_error', handleDeleteError);
      window.socket.off('chat_deleted', handleChatDeleted);
    };
  }, [dispatch]);

  const handleClose = () => {
    onClose();
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    if (!ENABLE_MODAL_SWIPE_BACK) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    if (touchStartX.current && touchEndX.current && touchEndX.current - touchStartX.current > 50) {
      onClose();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleSelect = (chat) => {
    dispatch({ type: 'SET_CURRENT_CHAT', payload: chat });
    dispatch({ type: 'SET_MESSAGES', payload: [] });
    dispatch({ type: 'MARK_CHAT_AS_READ', payload: { chatId: chat.id, participants: chat.participants } });
    if (window.socket && window.socket.connected) {
      window.socket.emit('join_chat', chat.id);
      window.socket.emit('get_chat_messages', chat.id);
      window.socket.emit('get_pinned_messages', chat.id);
      window.socket.emit('mark_chat_read', chat.id);
    }
    if (onOpenChat) onOpenChat();
    onClose();
  };

  const handleCreateChat = () => {
    if (onOpenCreateChat) {
      onOpenCreateChat();
    } else {
      dispatch({ type: 'TOGGLE_MODAL', payload: { modal: 'createChat', show: true } });
    }
  };

  const handleDeleteChat = (chatId, chatName, event) => {
    event.stopPropagation();
    if (!window.confirm(`Вы уверены, что хотите удалить чат "${chatName}"?`)) return;

    playDeleteSound(0.3);

    const chatToDelete = chats.find(chat => chat.id === chatId);
    if (chatToDelete) deletedChatBackup.current = chatToDelete;

    setDeletingChats(prev => new Set([...prev, chatId]));

    // Убираем сразу, чтобы список обновился без задержки
    dispatch({ type: 'DELETE_CHAT', payload: { chatId } });

    if (state.currentChat?.id === chatId) {
      dispatch({ type: 'SET_CURRENT_CHAT', payload: null });
      dispatch({ type: 'SET_MESSAGES', payload: [] });
    }

    if (window.socket && window.socket.connected) {
      window.socket.emit('delete_chat', { chatId });
    } else {
      alert('Нет соединения с сервером');
      setDeletingChats(prev => {
        const newSet = new Set(prev);
        newSet.delete(chatId);
        return newSet;
      });
      if (chatToDelete) {
        dispatch({ type: 'SET_CHATS', payload: [...state.chats, chatToDelete] });
      }
      deletedChatBackup.current = null;
      return;
    }

    setTimeout(() => {
      setDeletingChats(prev => {
        const newSet = new Set(prev);
        newSet.delete(chatId);
        return newSet;
      });
      deletedChatBackup.current = null;
    }, 5000);
  };

  // Обработчик клика по сотруднику
  const handleSelectEmployee = (userId) => {
    const otherUser = employees.find(e => e.id === userId);
    dispatch({ type: 'SET_LAST_PRIVATE_TARGET', payload: otherUser });

    const privateChat = (chats || []).find(
      chat => chat.isPrivate && chat.participants?.some(p => p.id === userId)
    );

    if (privateChat) {
      handleSelect(privateChat);
    } else {
      setPendingPrivateTargets(prev => {
        const next = new Set(prev);
        next.add(userId);
        return next;
      });

      if (window.socket) {
        window.socket.emit('create_private_chat', { targetUserId: userId });
      }
    }
  };

  const handleRefreshStatuses = async () => {
    if (!window.socket || isRefreshing) return;

    setIsRefreshing(true);
    try {
      window.socket.emit('get_all_users');
      window.socket.emit('get_online_users');
      setTimeout(() => setIsRefreshing(false), 1000);
    } catch (error) {
      console.error('Ошибка при обновлении статусов:', error);
      setIsRefreshing(false);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const query = search.trim().toLowerCase();
    const isOnline = isUserOnline(emp.id);

    if (filter === 'online' && !isOnline) return false;
    if (filter === 'offline' && isOnline) return false;

    if (!query) return true;
    return (
      (emp.username && emp.username.toLowerCase().includes(query)) ||
      (emp.first_name && emp.first_name.toLowerCase().includes(query)) ||
      (emp.last_name && emp.last_name.toLowerCase().includes(query))
    );
  });

  const onlineCount = employees.filter(emp => isUserOnline(emp.id)).length;

  if (!open) return null;

  const clockTime = now.toLocaleTimeString('ru-RU');
  const clockWeekday = capitalize(now.toLocaleDateString('ru-RU', { weekday: 'long' }));
  const clockDate = now.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 100002, display: 'flex' }}
      {...(ENABLE_MODAL_SWIPE_BACK ? { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd } : {})}
    >
      <div className="nd-empModal nd-mobile">
        <div className="nd-empHead">
          <button type="button" className="nd-hIco" onClick={handleClose} title="Вернуться в меню">
            <FiArrowLeft />
          </button>
          <h2>Чаты и сотрудники</h2>
          <div className="nd-empClock">
            <span className="nd-t nd-mono">{clockTime}</span>
            <i />
            <span className="nd-d">{clockWeekday}</span>
            <i />
            <span className="nd-d">{clockDate}</span>
          </div>
          <button
            type="button"
            className="nd-themeBtn"
            onClick={toggleNdTheme}
            title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
          >
            {ndIsLight ? <FiMoon /> : <FiSun />}
          </button>
          <button type="button" className="nd-empClose" onClick={handleClose} aria-label="Закрыть" title="Закрыть">
            <FiX />
          </button>
        </div>

        <div className="nd-empBody">
          <div className="nd-chipRow">
            <button
              type="button"
              className={`nd-fChip${filter === 'all' ? ' on' : ''}`}
              onClick={() => setFilter('all')}
              title="Все сотрудники"
            >
              <FiUsers size={13} /> Все <span className="nd-cN">{employees.length}</span>
            </button>
            <button
              type="button"
              className={`nd-fChip${filter === 'online' ? ' on' : ''}`}
              onClick={() => setFilter('online')}
              title="Только онлайн"
            >
              <FiUserCheck size={13} /> Онлайн <span className="nd-cN">{onlineCount}</span>
            </button>
            <button
              type="button"
              className={`nd-fChip${filter === 'offline' ? ' on' : ''}`}
              onClick={() => setFilter('offline')}
              title="Только офлайн"
            >
              <FiUserX size={13} /> Офлайн <span className="nd-cN">{employees.length - onlineCount}</span>
            </button>
            <button type="button" className="nd-fChip create" onClick={handleCreateChat} title="Создать чат">
              <FiPlus size={13} /> Создать чат
            </button>
          </div>

          <section>
            <div className="nd-secHead" style={{ marginBottom: 10 }}>
              <h3>Ваши чаты</h3>
            </div>
            <div className="nd-chatsPanel nd-glass">
              {visibleChats.length === 0 ? (
                <div className="nd-empEmpty">Нет чатов</div>
              ) : (
                visibleChats.map((chat, i) => (
                  <div
                    key={chat.id}
                    className={`nd-chatRow${chat.id === currentChatId ? ' nd-active' : ''}`}
                    style={{ animationDelay: `${Math.min(i * 60, 400)}ms`, opacity: deletingChats.has(chat.id) ? 0.5 : undefined }}
                    onClick={() => handleSelect(chat)}
                  >
                    <span className="nd-ci"><FiMessageSquare /></span>
                    <span className="nd-chName">{chat.name}</span>
                    {Number(chat.unread_count) > 0 && (
                      <span className="nd-chBadge">{chat.unread_count}</span>
                    )}
                    <button
                      type="button"
                      className="nd-del"
                      title="Удалить чат"
                      onClick={e => handleDeleteChat(chat.id, chat.name, e)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>

          <section>
            <div className="nd-secHead" style={{ justifyContent: 'center', marginBottom: 12 }}>
              <h3>Все сотрудники</h3>
              <button
                type="button"
                className={`nd-refBtn${isRefreshing ? ' nd-spin' : ''}`}
                onClick={handleRefreshStatuses}
                disabled={isRefreshing}
                title="Обновить статусы пользователей"
              >
                <span className="nd-sp"><FiRefreshCw size={13} /></span>
                {isRefreshing ? 'Обновление…' : 'Обновить'}
              </button>
            </div>

            <div className="nd-empSearchW">
              <span className="nd-sIco"><FiSearch /></span>
              <input
                type="text"
                placeholder="Поиск по имени или фамилии…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <div style={{ height: 14 }} />

            <div className="nd-empGrid">
              {filteredEmployees.length === 0 ? (
                <div className="nd-empEmpty">Ничего не найдено</div>
              ) : (
                filteredEmployees.map((emp, index) => {
                  const isOnline = isUserOnline(emp.id);
                  const displayName = emp.username || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Без имени';
                  const cardColor = ndColorOf(displayName);
                  const unread = state.unreadByUser?.[emp.id] || 0;
                  const missed = (state.missedCallsByUser || {})[emp.id] || 0;
                  const privateChat = (chats || []).find(chat =>
                    isOneToOnePrivate(chat) && chat.participants?.some(p => p.id === emp.id)
                  );
                  const hasChat = Boolean(privateChat) || pendingPrivateTargets.has(emp.id) || unread > 0;

                  return (
                    <div
                      key={emp.id}
                      className="nd-empCard nd-glass"
                      style={{ '--cc': cardColor, animationDelay: `${Math.min(index * 45, 500)}ms` }}
                      onClick={() => handleSelectEmployee(emp.id)}
                    >
                      <div className="nd-avWrap">
                        <div className="nd-av">
                          {emp.avatar ? <img src={emp.avatar} alt="" /> : (ndInitials(displayName) || <FiUser />)}
                        </div>
                        <span className={`nd-avSt ${isOnline ? 'nd-on' : 'nd-off'}`} />
                      </div>

                      <b>{displayName}</b>

                      <span className={`nd-stPill ${isOnline ? 'nd-on' : 'nd-off'}`}>
                        <i />
                        {formatLastSeen(emp.last_seen, isOnline)}
                      </span>

                      {(unread > 0 || missed > 0 || hasChat) && (
                        <div className="nd-empBadges">
                          {hasChat && unread === 0 && (
                            <span className="nd-empBadge nd-chatMark" title="Есть переписка"><FiMessageSquare size={11} /> переписка</span>
                          )}
                          {unread > 0 && (
                            <span className="nd-empBadge" title="Непрочитанные сообщения">{unread} сообщ.</span>
                          )}
                          {missed > 0 && (
                            <span className="nd-empBadge nd-miss" title="Пропущенные звонки">{missed} пропущ. звонков</span>
                          )}
                        </div>
                      )}

                      {state.user?.id !== emp.id && (
                        <div className="nd-cardBtns">
                          <button
                            type="button"
                            className="nd-wBtn"
                            title="Написать сообщение"
                            onClick={(e) => { e.stopPropagation(); handleSelectEmployee(emp.id); }}
                          >
                            <FiMessageSquare size={14} /> Написать
                          </button>
                          <button
                            type="button"
                            className="nd-cBtn"
                            title="Позвонить"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!window.socket) return;
                              dispatch({ type: 'SET_VOICE_CALL', payload: { status: 'outgoing', toUser: emp, isCaller: true } });
                              window.socket.emit('voice_call:request', { toUserId: emp.id });
                            }}
                          >
                            <FiPhone size={14} /> Позвонить
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}
